import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";

const MASJID_CACHE_KEY = "v2_masjid_list_cache";
const FIRST_PAGE_CACHE_KEY = "v3_public_masjid_first_page";
const HOME_CACHE_KEY = "etikaf_public_home_cache_v2";

const pagePromises = new Map();
const slugPromises = new Map();
const feedResults = new Map();
let homePromise = null;

const readCache = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

const writeCache = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browser storage is an optional optimization.
  }
};

const normalizeSummary = (documentIdValue, value = {}) => {
  const id = value.mosqueId || documentIdValue;
  const name = value.name || value.profail?.nama || "Tanpa Nama";
  const slug = value.slug || value.profail?.slug || id;
  const logo = value.thumbnailUrl || value.logoUrl || value.bannerUrl || "";
  const banner = value.bannerUrl || value.thumbnailUrl || value.logoUrl || "";
  const city = value.city || value.locality || value.profail?.bandar || "";
  const state = value.state || value.profail?.negeri || "Lain-lain";

  return {
    ...value,
    id,
    mosqueId: id,
    slug,
    name,
    nameKey: value.nameKey || name.trim().toLowerCase(),
    photo_src: logo,
    address: value.address || value.profail?.alamat || "",
    locality: city,
    city,
    state,
    prayerZone: value.prayerZone || value.profail?.zone || "SGR01",
    type: value.type || value.profail?.jenis || "masjid",
    thumbnailUrl: value.thumbnailUrl || logo,
    logoUrl: value.logoUrl || logo,
    bannerUrl: banner,
    isPublished: value.isPublished !== false,
    isEtikafActive: value.isPublished !== false && value.isEtikafActive !== false,
    profail: {
      ...(value.profail || {}),
      nama: name,
      slug,
      alamat: value.address || value.profail?.alamat || "",
      bandar: city,
      negeri: state,
      zone: value.prayerZone || value.profail?.zone || "SGR01",
      jenis: value.type || value.profail?.jenis || "masjid",
      logo,
      gambar: banner,
    },
  };
};

const pageKey = ({ after, pageSize }) =>
  `${after?.nameKey || "first"}:${after?.documentId || "first"}:${pageSize}`;

export const getCachedPublicMasjids = () => {
  const firstPage = readCache(FIRST_PAGE_CACHE_KEY);
  if (Array.isArray(firstPage?.items)) return firstPage.items;
  const legacyCache = readCache(MASJID_CACHE_KEY);
  return Array.isArray(legacyCache) ? legacyCache : [];
};

export const loadPublicMasjidPage = ({ after = null, pageSize = 30 } = {}) => {
  const key = pageKey({ after, pageSize });
  if (pagePromises.has(key)) return pagePromises.get(key);

  const constraints = [
    where("isPublished", "==", true),
    orderBy("nameKey", "asc"),
    orderBy(documentId(), "asc"),
  ];
  if (after) constraints.push(startAfter(after.nameKey, after.documentId));
  constraints.push(limit(pageSize));

  const promise = getDocs(query(collection(dbAdmin, "publicMasajid"), ...constraints))
    .then((snapshot) => {
      const items = snapshot.docs
        .map((document) => normalizeSummary(document.id, document.data()))
        .filter((item) => item.isPublished);
      const last = items[items.length - 1] || null;
      const result = {
        items,
        nextCursor: last
          ? { nameKey: last.nameKey, documentId: last.id }
          : null,
        hasMore: snapshot.docs.length >= pageSize,
        source: "publicMasajid",
      };
      if (!after) {
        writeCache(FIRST_PAGE_CACHE_KEY, result);
        writeCache(MASJID_CACHE_KEY, items);
      }
      return result;
    });
  pagePromises.set(key, promise);
  return promise;
};

export const loadPublicMasjids = () =>
  loadPublicMasjidPage().then((page) => page.items);

export const loadPublicMasjidBySlug = (slug) => {
  const normalizedSlug = String(slug || "").trim().toLowerCase();
  if (!normalizedSlug) return Promise.resolve(null);
  if (slugPromises.has(normalizedSlug)) return slugPromises.get(normalizedSlug);

  const promise = getDocs(query(
    collection(dbAdmin, "publicMasajid"),
    where("slug", "==", normalizedSlug),
    where("isPublished", "==", true),
    limit(1)
  )).then((snapshot) => {
    const document = snapshot.docs[0];
    return document ? normalizeSummary(document.id, document.data()) : null;
  });
  slugPromises.set(normalizedSlug, promise);
  return promise;
};

export const loadLegacyMasjidById = async (mosqueId) => {
  const snapshot = await getDoc(doc(dbAdmin, "masajid", mosqueId));
  return snapshot.exists() ? { id: snapshot.id || mosqueId, ...snapshot.data() } : null;
};

const isValidPublicWebFeed = (feed, mosqueId) => (
  feed
  && typeof feed === "object"
  && feed.schemaVersion === 1
  && feed.mosqueId === mosqueId
  && feed.profile
  && typeof feed.profile === "object"
  && !Array.isArray(feed.profile)
  && ["events", "posters", "announcements", "campaigns"]
    .every((field) => Array.isArray(feed[field]))
);

export const loadPublicWebFeed = async (
  mosqueId,
  { legacyFallback = null } = {}
) => {
  if (feedResults.has(mosqueId)) return feedResults.get(mosqueId);
  let reason = null;
  try {
    const snapshot = await getDoc(doc(dbAdmin, "masajid", mosqueId, "public", "web"));
    if (snapshot.exists()) {
      const feed = snapshot.data();
      if (isValidPublicWebFeed(feed, mosqueId)) {
        const result = { feed, source: "publicWeb", fallbackReason: null };
        feedResults.set(mosqueId, result);
        return result;
      }
      reason = "invalid-schema";
    } else {
      reason = "missing";
    }
  } catch (error) {
    reason = error?.code === "permission-denied" ? "forbidden" : "unavailable";
  }

  const legacy = legacyFallback ? await legacyFallback(mosqueId) : null;
  const result = {
    feed: legacy,
    source: legacyFallback ? "legacyFallback" : "publicWeb",
    fallbackReason: reason,
  };
  feedResults.set(mosqueId, result);
  return result;
};

export const toPublicMasjidViewModel = (summary = {}, feed = {}) => {
  const profile = feed.profile || {};
  const posters = Array.isArray(feed.posters) ? feed.posters : [];
  const events = Array.isArray(feed.events) ? feed.events : [];
  const announcements = Array.isArray(feed.announcements) ? feed.announcements : [];
  const campaigns = Array.isArray(feed.campaigns) ? feed.campaigns : [];
  const posterUrls = posters
    .map((poster) => typeof poster === "string"
      ? poster
      : poster.mediumUrl || poster.thumbnailUrl || poster.posterImage || "")
    .filter(Boolean);
  const catalogPosterUrls = Array.isArray(summary.etvImages)
    ? summary.etvImages.filter(Boolean)
    : [];
  const feedEtvImageUrls = Array.isArray(feed.etvImages)
    ? feed.etvImages.filter(Boolean)
    : [];
  const etvImageUrls = feedEtvImageUrls.length > 0
    ? feedEtvImageUrls
    : posterUrls.length > 0 ? posterUrls : catalogPosterUrls;
  const id = summary.mosqueId || summary.id || feed.mosqueId || "";

  return {
    id,
    mosqueId: id,
    slug: summary.slug || profile.slug || id,
    name: summary.name || profile.nama || "Tanpa Nama",
    nameKey: summary.nameKey || "",
    photo_src: summary.photo_src || profile.logo || profile.logoJa || "",
    address: summary.address || profile.alamat || "",
    locality: summary.locality || summary.city || profile.bandar || "",
    city: summary.city || profile.bandar || "",
    state: summary.state || profile.negeri || "Lain-lain",
    prayerZone: summary.prayerZone || "SGR01",
    type: summary.type || "masjid",
    thumbnailUrl: summary.thumbnailUrl || "",
    logoUrl: summary.logoUrl || profile.logo || profile.logoJa || "",
    bannerUrl: summary.bannerUrl || profile.gambar || "",
    isPublished: summary.isPublished !== false,
    isEtikafActive: summary.isEtikafActive !== false,
    nama: profile.nama || summary.name || "Tanpa Nama",
    logo: profile.logo || profile.logoJa || "",
    gambar: profile.gambar || "",
    profile,
    profail: profile,
    posters,
    events,
    announcements,
    aktiviti: { kuliah: events, program: posters },
    mesej: announcements,
    kempen: { senarai: campaigns },
    etv: { etvImage: etvImageUrls },
    schemaVersion: feed.schemaVersion,
    contentVersion: feed.contentVersion || 0,
  };
};

const publicEventStartMs = (event = {}) => {
  const value = event.start || event.startsAt || event.date;
  if (value instanceof Date) return value.getTime();
  if (value?.toDate) return value.toDate().getTime();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const flattenPublicHomeEvents = (items) => (
  Array.isArray(items) ? items : []
).flatMap((item) => {
  const mosqueId = item?.mosqueId || item?.id || "";
  const mosque = normalizeSummary(mosqueId, item?.mosque || {});
  return (Array.isArray(item?.events) ? item.events : [])
    .map((event) => ({ ...event, masjidId: mosqueId, masjid: mosque }));
}).filter((event) => publicEventStartMs(event) > 0)
  .sort((left, right) => (
    publicEventStartMs(left) - publicEventStartMs(right)
    || String(left.id || "").localeCompare(String(right.id || ""))
  ))
  .slice(0, 12);

export const resolvePublicHomeMasjid = (masjids, event) => (
  (Array.isArray(masjids) ? masjids : [])
    .find((mosque) => mosque.id === event?.masjidId)
  || event?.masjid
  || null
);

export const findEtvMosqueName = (masjids, imageUrl) => {
  const mosque = (Array.isArray(masjids) ? masjids : []).find((item) => {
    const legacyImages = Array.isArray(item?.etv?.etvImage) ? item.etv.etvImage : [];
    const summaryImages = Array.isArray(item?.etvImages) ? item.etvImages : [];
    return legacyImages.includes(imageUrl) || summaryImages.includes(imageUrl);
  });
  return mosque?.profail?.nama || mosque?.name || "";
};

export const loadPublicMasjidView = async (
  summary,
  { legacyFallback = loadLegacyMasjidById } = {}
) => {
  const mosqueId = summary?.mosqueId || summary?.id || "";
  const result = await loadPublicWebFeed(mosqueId, { legacyFallback });
  if (result.source === "legacyFallback") {
    return {
      masjid: result.feed ? {
        ...result.feed,
        id: mosqueId,
        mosqueId,
        slug: summary?.slug || result.feed.slug || mosqueId,
      } : null,
      source: result.source,
      fallbackReason: result.fallbackReason,
      schemaVersion: Number(result.feed?.schemaVersion || 0),
    };
  }
  return {
    masjid: toPublicMasjidViewModel(summary, result.feed),
    source: result.source,
    fallbackReason: result.fallbackReason,
    schemaVersion: Number(result.feed?.schemaVersion || 0),
  };
};

export const getCachedPublicHome = () => {
  const value = readCache(HOME_CACHE_KEY);
  return Array.isArray(value) ? value : null;
};

export const loadPublicHome = ({ now = new Date() } = {}) => {
  if (!homePromise) {
    homePromise = getDocs(query(
      collection(dbAdmin, "publicHome"),
      where("nextEventAt", ">=", now),
      orderBy("nextEventAt", "asc"),
      limit(12)
    ))
      .then((snapshot) => {
        const items = snapshot.docs.map((document) => ({
          id: document.id,
          ...document.data(),
        }));
        writeCache(HOME_CACHE_KEY, items);
        return items;
      })
      .catch(() => []);
  }
  return homePromise;
};

export const __resetPublicMasjidCatalogForTests = () => {
  pagePromises.clear();
  slugPromises.clear();
  feedResults.clear();
  homePromise = null;
};
